/**
 * Supplement Library - Educational information about vitamins, minerals, and supplements
 * commonly discussed in integrative health contexts
 */

export const supplements = [
  // VITAMINS
  {
    id: 'vitamin-d3',
    name: 'Vitamin D3',
    category: 'Vitamin',
    whyIncluded: 'Commonly discussed in integrative health contexts. Often included for general nutritional support.',
    generalNotes: 'Available in various forms including capsules, liquid drops, and soft gels. Fat-soluble vitamin. Some people obtain vitamin D through sunlight exposure and dietary sources.',
    cautions: 'Individual tolerance varies. Some supplements may interact with medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: []
  },
  {
    id: 'vitamin-b12',
    name: 'Vitamin B12',
    category: 'Vitamin',
    whyIncluded: 'Often discussed in dietary contexts, particularly among those following plant-based eating patterns.',
    generalNotes: 'Available in various forms including tablets, sublingual forms, and liquid. Water-soluble vitamin. Found naturally in certain foods including fish and sea vegetables.',
    cautions: 'Individual tolerance varies. Some supplements may interact with medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['seaweed']
  },
  {
    id: 'vitamin-c',
    name: 'Vitamin C',
    category: 'Vitamin',
    whyIncluded: 'Commonly discussed in integrative health contexts. Often included for general nutritional support.',
    generalNotes: 'Available in various forms including tablets, capsules, powders, and liquid. Water-soluble vitamin. Found naturally in many fruits and vegetables.',
    cautions: 'Individual tolerance varies. High amounts may cause digestive discomfort in some people. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['broccoli', 'kale', 'watercress']
  },
  {
    id: 'vitamin-k',
    name: 'Vitamin K',
    category: 'Vitamin',
    whyIncluded: 'Often discussed in nutritional contexts. Available in K1 and K2 forms.',
    generalNotes: 'Available in various forms including capsules and tablets. Fat-soluble vitamin. Found naturally in leafy greens and fermented foods.',
    cautions: 'Individual tolerance varies. May interact with blood-thinning medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation, especially if taking anticoagulants.',
    relatedFoods: ['kale', 'spinach', 'watercress']
  },

  // MINERALS
  {
    id: 'iron',
    name: 'Iron',
    category: 'Mineral',
    whyIncluded: 'Often discussed in dietary and nutritional contexts.',
    generalNotes: 'Available in various forms including ferrous and non-ferrous types. Absorption may vary by form. Found naturally in many whole foods including legumes and leafy greens.',
    cautions: 'Individual tolerance varies. Some forms may cause digestive discomfort. Can interact with certain medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['lentils', 'spinach', 'chickpeas', 'pumpkin-seeds']
  },
  {
    id: 'magnesium',
    name: 'Magnesium',
    category: 'Mineral',
    whyIncluded: 'Commonly discussed in integrative health contexts. Often included for general nutritional support.',
    generalNotes: 'Available in various forms including citrate, glycinate, and oxide. Different forms may have different absorption characteristics. Found naturally in many whole foods.',
    cautions: 'Individual tolerance varies. High amounts may cause digestive effects in some people. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['spinach', 'pumpkin-seeds', 'almonds', 'brown-rice']
  },
  {
    id: 'zinc',
    name: 'Zinc',
    category: 'Mineral',
    whyIncluded: 'Often discussed in nutritional contexts. Commonly included for general nutritional support.',
    generalNotes: 'Available in various forms including picolinate, gluconate, and citrate. Found naturally in seeds, nuts, and whole grains.',
    cautions: 'Individual tolerance varies. High amounts may interfere with copper absorption. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['pumpkin-seeds', 'sesame-seeds']
  },
  {
    id: 'selenium',
    name: 'Selenium',
    category: 'Mineral',
    whyIncluded: 'Often discussed in integrative health contexts.',
    generalNotes: 'Available in various forms including selenomethionine and sodium selenite. Found naturally in certain whole grains and seeds.',
    cautions: 'Individual tolerance varies. Narrow range between adequate and excessive intake. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['brown-rice']
  },

  // HERBAL / PLANT-BASED
  {
    id: 'green-tea-egcg',
    name: 'Green Tea Extract (EGCG)',
    category: 'Herbal',
    whyIncluded: 'Studied in clinical trials at Mayo Clinic for CLL patients. Research showed 69% of early-stage CLL patients had a biological response to EGCG.',
    generalNotes: 'EGCG (epigallocatechin gallate) is the major active component of green tea. Mayo Clinic conducted phase I and II clinical trials showing it may help reduce lymphocyte counts and lymph node size in watch-and-wait CLL patients. The extract used in trials was pharmaceutical-grade, different from over-the-counter supplements.',
    cautions: 'Not a substitute for chemotherapy. Over-the-counter supplements vary in quality and concentration. Contains caffeine unless decaffeinated. Always consult your oncologist before use and ensure appropriate laboratory monitoring.',
    relatedFoods: ['green-tea'],
    hasResearchPage: true,
    researchPageUrl: '/green-tea-research'
  },
  {
    id: 'turmeric-supplement',
    name: 'Turmeric',
    category: 'Herbal',
    whyIncluded: 'Traditionally used in various dietary practices. Often discussed in integrative health contexts.',
    generalNotes: 'Available in various forms including capsules, tablets, and extracts. Often combined with black pepper for absorption. Different from culinary turmeric use.',
    cautions: 'Individual tolerance varies. May cause digestive discomfort in some people. May interact with certain medications including blood thinners. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['turmeric-tea']
  },
  {
    id: 'astragalus',
    name: 'Astragalus',
    category: 'Herbal',
    whyIncluded: 'Traditionally used in various dietary practices. Sometimes discussed in integrative health contexts.',
    generalNotes: 'Available in various forms including capsules, tablets, and teas. Traditional herb used in certain cultural practices.',
    cautions: 'Individual tolerance varies. May interact with certain medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: []
  },
  {
    id: 'papaya-leaf',
    name: 'Papaya Leaf Extract',
    category: 'Herbal',
    whyIncluded: 'Sometimes discussed in certain health communities. Traditional use in some cultural practices.',
    generalNotes: 'Available in various forms including capsules, tablets, and liquid extracts. Derived from papaya plant leaves.',
    cautions: 'Individual tolerance varies. Limited research on safety and interactions. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: []
  },

  // AMINO ACIDS / OTHER
  {
    id: 'lysine',
    name: 'Lysine',
    category: 'Amino Acid',
    whyIncluded: 'Essential amino acid sometimes discussed in nutritional contexts.',
    generalNotes: 'Available in various forms including tablets, capsules, and powders. Amino acid found naturally in protein-containing foods.',
    cautions: 'Individual tolerance varies. May interact with certain medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: ['lentils', 'chickpeas']
  },
  {
    id: 'multivitamin',
    name: 'Multivitamin (General)',
    category: 'Other',
    whyIncluded: 'Commonly discussed for general nutritional support. Often includes various vitamins and minerals in one formulation.',
    generalNotes: 'Available in various forms including tablets, capsules, gummies, and liquids. Formulations vary widely by brand and intended use. Some people prefer whole-food based versions.',
    cautions: 'Individual tolerance varies. Quality and formulation vary significantly between products. Some vitamins and minerals may interact with medications. Always discuss supplement use with a qualified healthcare provider before beginning any supplementation.',
    relatedFoods: []
  }
];

export const supplementCategories = [
  'Vitamin',
  'Mineral',
  'Herbal',
  'Tea / Beverage',
  'Amino Acid',
  'Other'
];
